/**
 * Admin welcome card: compact dismissible greeting.
 * Shows greeting with user name, dismissible via X button.
 *
 * Uses sessionStorage so it appears on each new app launch,
 * but stays dismissed when app is minimized/backgrounded.
 */

import { useState, useEffect } from "react";
import { IconX } from "../../../components/Icons.tsx";
import { useAuth } from "../../../lib/auth.tsx";

const STORAGE_KEY = "admin_welcome_dismissed";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Доброе утро";
  if (hour >= 12 && hour < 17) return "Добрый день";
  if (hour >= 17 && hour < 22) return "Добрый вечер";
  return "Доброй ночи";
}

/** Имя для приветствия: firstName → lastName → @username → «друг» */
function getDisplayNameForGreeting(account: {
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}): string {
  const first = account.firstName?.trim();
  const last = account.lastName?.trim();
  if (first) return first;
  if (last) return last;
  if (account.username?.trim()) return `@${account.username.trim()}`;
  return "друг";
}

export function AdminWelcomeCard() {
  const { account } = useAuth();
  const [isDismissed, setIsDismissed] = useState(true); // Start hidden to avoid flash

  // Check sessionStorage on mount
  useEffect(() => {
    // sessionStorage is cleared when app is closed, but persists during minimize/background
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    setIsDismissed(dismissed === "true");
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(STORAGE_KEY, "true");
  };

  if (isDismissed) {
    return null;
  }

  const displayName = account ? getDisplayNameForGreeting(account) : "";
  const greeting = getGreeting();

  return (
    <div className="flex items-center gap-3 py-3 px-4 mb-4 rounded-xl bg-amber-50 border border-amber-100">
      <span className="text-2xl" role="img" aria-label="pray">
        🙏
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-amber-600 font-medium uppercase tracking-wide">
          Благодарим за твоё служение
        </p>
        <p className="text-[15px] text-amber-900 font-semibold truncate">
          {greeting}
          {displayName ? `, ${displayName}` : ""}!
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-amber-400 hover:text-amber-600 hover:bg-amber-100 transition-colors shrink-0"
        aria-label="Закрыть"
      >
        <IconX className="w-4 h-4" />
      </button>
    </div>
  );
}
